import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../services/api';

const BacktestForm = ({ strategies, symbols, onSubmit, isRunning }) => {
  const [formData, setFormData] = useState({
    strategy_name: '',
    symbol: '',
    start_date: new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
    end_date: new Date(),
    parameters: {}
  });
  
  const [userSettings, setUserSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [strategyParams, setStrategyParams] = useState({});
  
  // Fetch user settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await apiService.settings.get();
        setUserSettings(response.data);
        
        // Apply default settings if available
        if (response.data.default_symbol) {
          setFormData(prev => ({ ...prev, symbol: response.data.default_symbol }));
        }
        
        if (response.data.default_strategy) {
          setFormData(prev => ({ ...prev, strategy_name: response.data.default_strategy }));
        }
        
        if (response.data.default_start_date) {
          setFormData(prev => ({ ...prev, start_date: new Date(response.data.default_start_date) }));
        }
        
        if (response.data.default_end_date) {
          setFormData(prev => ({ ...prev, end_date: new Date(response.data.default_end_date) }));
        }
      } catch (err) {
        console.error('Error fetching user settings:', err);
      } finally {
        setLoadingSettings(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Set default strategy parameters based on selected strategy
  useEffect(() => {
    if (formData.strategy_name) {
      // Here we would typically fetch strategy-specific parameters from the backend
      // For now, we'll use some hardcoded parameters based on strategy name
      let params = {};
      
      switch (formData.strategy_name.toLowerCase()) {
        case 'supertrend_combined_premium':
        case 'supertrendcombinedpremium':
        case 'supertrend_combined_premium_v2':
        case 'supertrend_combined_premium_optimized':
          params = {
            entry_time: '09:30',
            exit_time: '15:25',
            call_strike_offset: -150,
            put_strike_offset: -150,
            stoploss_percentage: 20,
            reexecute_times: 1,
            reexecute_time: '11:32'
          };
          break;
          
        case 'fractal':
        case 'fractalstrategy':
        case 'fractal_strategy_simple':  // Add support for our new optimized strategy
          params = {
            timeframe: 5,
            strike_selection: 'ITM-1',  // Default to ITM-1
            carry_forward: false,  // Default to disabled
            backtest_on: 'option'  // Default to option trading
          };
          break;
          
        case 'daily_trading_range':
        case 'dailytradingrange':
          params = {
            range_factor_1: 0.55,
            range_factor_2: 0.35
          };
          break;
        
        case 'supertrend_15_05':
          params = {
            timeframe: 60,
            supertrend_factor: 5,
            supertrend_length: 15,
            entry_time: '10:15',
            exit_time: '15:15',
            delta_target: 0.35,
            hedge_offset: 100,
            apply_min_credit: true,
            min_credit_amount: 2000
          };
          break;
          
        case 'rsi_futures_hedged_strategy':
        case 'rsifutureshedgedstrategy':
          params = {
            rsi_period: 14,
            rsi_entry_level: 50,
            rsi_upper_exit_level: 75,
            rsi_lower_exit_level: 25,
            hedge_ratio: 0.5,
            hedge_strike_offset: 0,
            max_loss_percent: 2.0,
            target_profit_percent: 3.0,
            max_trades_per_day: 2,
            dynamic_hedge: true,
            trail_exit: true,
            initial_capital: 1000000
          };
          break;
          
        case 'hekin_ashi_strategy':
        case 'hekinashistrategy':
          params = {
            entry_start_time: '09:30',
            entry_end_time: '12:00',
            exit_time: '15:25',
            atm_time: '09:15',
            quantity: 2,
            max_trades_per_day: 2,
            bb_period: 20,
            bb_std_dev: 2.0,
            sl_percentage: 0.03,
            entry_percentage: 0.03,
            exit_option: 'time_exit'
          };
          break;
        
        case 'banknifty_wma_lr_strategy':
        case 'bankniftywmalrstrategy':
          params = {
            timeframe: 10,
            vwap_period: 460,
            wma_period: 460,
            lr_period: 460,
            sd_period: 460,
            sensitivity_f: 0.85,  // Can also use 0.8
            lookback_candles: 16,
            time_exit_candles: 270,
            entry_start_time: '09:30',
            entry_end_time: '14:50',
            time_exit_time: '14:50',
            entry_mode: 'close',
            quantity: 1
          };
          break;
          
        default:
          params = {};
          break;
      }
      
      setStrategyParams(params);
      setFormData(prev => ({ ...prev, parameters: params }));
    }
  }, [formData.strategy_name]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle parameter changes
  const handleParamChange = (e) => {
    const { name, value, type } = e.target;
    let paramValue;
    
    if (type === 'number') {
      paramValue = parseFloat(value);
    } else if (name === 'carry_forward' || name === 'apply_min_credit') {
      // Handle boolean conversion for carry_forward/apply_min_credit
      paramValue = (value === 'true') || (value === true);
    } else {
      paramValue = value;
    }
    
    setFormData(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [name]: paramValue
      }
    }));
  };
  
  // Handle date changes
  const handleDateChange = (date, name) => {
    setFormData(prev => ({ ...prev, [name]: date }));
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Format dates for API
    const formattedData = {
      ...formData,
      start_date: formData.start_date.toISOString().split('T')[0],
      end_date: formData.end_date.toISOString().split('T')[0],
      save_to_db: true
    };
    
    onSubmit(formattedData);
  };
  
  return (
    <form onSubmit={handleSubmit} className="px-4 py-5 sm:p-6">
      {loadingSettings ? (
        <p className="text-gray-500 text-center">Loading settings...</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Strategy selection */}
          <div>
            <label htmlFor="strategy_name" className="block text-sm font-medium text-gray-700">
              Strategy
            </label>
            <select
              id="strategy_name"
              name="strategy_name"
              value={formData.strategy_name}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              disabled={isRunning}
            >
              <option value="">Select a strategy</option>
              {strategies.map(strategy => (
                <option key={strategy.id} value={strategy.id}>
                  {strategy.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Symbol selection */}
          <div>
            <label htmlFor="symbol" className="block text-sm font-medium text-gray-700">
              Symbol
            </label>
            <select
              id="symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              disabled={isRunning}
            >
              <option value="">Select a symbol</option>
              {symbols.map(symbol => (
                <option key={symbol.id} value={symbol.id}>
                  {symbol.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Date Range */}
          <div>
            <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <DatePicker
              id="start_date"
              selected={formData.start_date}
              onChange={(date) => handleDateChange(date, 'start_date')}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              dateFormat="yyyy-MM-dd"
              maxDate={formData.end_date}
              disabled={isRunning}
            />
          </div>
          
          <div>
            <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <DatePicker
              id="end_date"
              selected={formData.end_date}
              onChange={(date) => handleDateChange(date, 'end_date')}
              className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              dateFormat="yyyy-MM-dd"
              minDate={formData.start_date}
              maxDate={new Date()}
              disabled={isRunning}
            />
          </div>
          
          {/* Strategy Parameters */}
          {formData.strategy_name && (
            <div className="md:col-span-2 mt-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Strategy Parameters</h4>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {Object.entries(strategyParams).map(([paramName, paramValue]) => {
                  // Special handling for strike_selection parameter
                  if (paramName === 'strike_selection') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Strike Selection
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          {/* In-the-money options (deeper to shallower) */}
                          <option value="ITM-10">ITM-10 (In-the-Money - 10 Strikes)</option>
                          <option value="ITM-9">ITM-9 (In-the-Money - 9 Strikes)</option>
                          <option value="ITM-8">ITM-8 (In-the-Money - 8 Strikes)</option>
                          <option value="ITM-7">ITM-7 (In-the-Money - 7 Strikes)</option>
                          <option value="ITM-6">ITM-6 (In-the-Money - 6 Strikes)</option>
                          <option value="ITM-5">ITM-5 (In-the-Money - 5 Strikes)</option>
                          <option value="ITM-4">ITM-4 (In-the-Money - 4 Strikes)</option>
                          <option value="ITM-3">ITM-3 (In-the-Money - 3 Strikes)</option>
                          <option value="ITM-2">ITM-2 (In-the-Money - 2 Strikes)</option>
                          <option value="ITM-1">ITM-1 (In-the-Money - 1 Strike)</option>
                          
                          {/* At-the-money option */}
                          <option value="ATM">ATM (At-The-Money)</option>
                          
                          {/* Out-of-the-money options (shallow to deeper) */}
                          <option value="OTM-1">OTM-1 (Out-of-the-Money - 1 Strike)</option>
                          <option value="OTM-2">OTM-2 (Out-of-the-Money - 2 Strikes)</option>
                          <option value="OTM-3">OTM-3 (Out-of-the-Money - 3 Strikes)</option>
                          <option value="OTM-4">OTM-4 (Out-of-the-Money - 4 Strikes)</option>
                          <option value="OTM-5">OTM-5 (Out-of-the-Money - 5 Strikes)</option>
                          <option value="OTM-6">OTM-6 (Out-of-the-Money - 6 Strikes)</option>
                          <option value="OTM-7">OTM-7 (Out-of-the-Money - 7 Strikes)</option>
                          <option value="OTM-8">OTM-8 (Out-of-the-Money - 8 Strikes)</option>
                          <option value="OTM-9">OTM-9 (Out-of-the-Money - 9 Strikes)</option>
                          <option value="OTM-10">OTM-10 (Out-of-the-Money - 10 Strikes)</option>
                        </select>
                      </div>
                    );
                  } 
                  // Special handling for timeframe parameter
                  else if (paramName === 'timeframe') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Timeframe (minutes)
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value="1">1 minute</option>
                          <option value="3">3 minutes</option>
                          <option value="5">5 minutes</option>
                          <option value="10">10 minutes</option>
                          <option value="12">12 minutes</option>
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="45">45 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="75">75 minutes</option>
                          <option value="1440">1 day</option>
                        </select>
                      </div>
                    );
                  }
                  // Special handling for backtest_on parameter
                  else if (paramName === 'backtest_on') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Backtest On
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value="option">Option</option>
                          <option value="spot">Spot</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Option: Trade options based on spot signals. Spot: Trade directly on spot prices.
                        </p>
                      </div>
                    );
                  }
                  // Special handling for carry_forward parameter
                  else if (paramName === 'carry_forward') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Carry Forward
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value={false}>Disabled</option>
                          <option value={true}>Enabled</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          When enabled, positions automatically roll to next expiry at 3:15 PM. When disabled, positions exit and wait for fresh signals.
                        </p>
                      </div>
                    );
                  }
                  // Special handling for apply_min_credit parameter
                  else if (paramName === 'apply_min_credit') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Apply Min Credit Filter
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={String(formData.parameters[paramName] ?? paramValue)}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value={'true'}>Enabled</option>
                          <option value={'false'}>Disabled</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          When enabled, take position only if net credit per lot exceeds the threshold.
                        </p>
                      </div>
                    );
                  }
                  // Special handling for entry_mode parameter (BankNifty WMA-LR Strategy)
                  else if (paramName === 'entry_mode') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Entry Mode
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value="close">Close of Signal Candle</option>
                          <option value="next_open">Open of Next Candle</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Enter at close of the signal candle or at open of the next candle.
                        </p>
                      </div>
                    );
                  }
                  // Special handling for exit_option parameter (Hekin Ashi Strategy)
                  else if (paramName === 'exit_option') {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          Exit Option
                        </label>
                        <select
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                        >
                          <option value="time_exit">Time Exit (3:15 PM)</option>
                          <option value="red_candle_break">Red Candle Break Below Signal Low</option>
                          <option value="ema_break">EMA 10 Break</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Choose exit condition for remaining quantity after first target is hit.
                        </p>
                      </div>
                    );
                  }
                  // Default handling for other parameters
                  else {
                    return (
                      <div key={paramName}>
                        <label htmlFor={paramName} className="block text-sm font-medium text-gray-700">
                          {paramName.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + paramName.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().slice(1)}
                        </label>
                        <input
                          type={typeof paramValue === 'number' ? 'number' : 'text'}
                          id={paramName}
                          name={paramName}
                          value={formData.parameters[paramName] || paramValue}
                          onChange={handleParamChange}
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          disabled={isRunning}
                          step={typeof paramValue === 'number' && paramValue % 1 !== 0 ? '0.01' : '1'}
                        />
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )}
          
          {/* Save as Default Settings */}
          <div className="md:col-span-2 mt-6">
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="save_settings"
                  name="save_settings"
                  type="checkbox"
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                  disabled={isRunning}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="save_settings" className="font-medium text-gray-700">
                  Save as default settings
                </label>
                <p className="text-gray-500">
                  These settings will be automatically loaded when you open the backtest page.
                </p>
              </div>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="md:col-span-2 mt-6">
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isRunning || !formData.strategy_name || !formData.symbol}
            >
              {isRunning ? 'Running Backtest...' : 'Run Backtest'}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default BacktestForm;
